import React, { useRef, useState, useEffect, useCallback } from "react";
import ReactDOM, { createPortal } from "react-dom";
import {
  FiSearch,
  FiChevronDown,
  FiCheck,
  FiUser,
  FiRefreshCw,
  FiAlertCircle,
  FiX,
  FiPlus,
  FiTrash2,
  FiCheckCircle,
  FiLoader,
} from "react-icons/fi";
import api from "../../../connector/api";
import Pagination from "../reuse/Pagination";
import StudentDetailTab from "./StudentDetailTab";

// Custom Dropdown Component
function CustomDropdown({ label, value, options, onChange, icon: Icon }) {
  const [isOpen, setIsOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0, width: 0 });
  const buttonRef = useRef(null);

  const selectedOption =
    options.find((opt) => opt.value === value) || options[0];

  const updateCoords = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setCoords({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
        width: Math.max(rect.width, 140),
      });
    }
  };

  const toggleDropdown = () => {
    if (!isOpen) {
      updateCoords();
    }
    setIsOpen(!isOpen);
  };

  useEffect(() => {
    const handleScrollOrResize = () => {
      if (isOpen) updateCoords();
    };
    const handleClickOutside = (e) => {
      if (
        buttonRef.current &&
        !buttonRef.current.contains(e.target) &&
        !e.target.closest(".portal-dropdown-menu")
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("scroll", handleScrollOrResize, true);
    window.addEventListener("resize", handleScrollOrResize);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", handleScrollOrResize, true);
      window.removeEventListener("resize", handleScrollOrResize);
    };
  }, [isOpen]);

  return (
    <div className="relative inline-block w-full">
      <button
        ref={buttonRef}
        type="button"
        onClick={toggleDropdown}
        className="w-full flex items-center justify-between gap-2 px-3 py-1.5 bg-zinc-50 border border-zinc-200/80 rounded-lg text-zinc-700 hover:bg-zinc-100/80 focus:outline-none transition-colors text-xs font-medium cursor-pointer"
      >
        <div className="flex items-center gap-2 truncate">
          {Icon && <Icon className="w-3.5 h-3.5 text-zinc-400 shrink-0" />}
          <span className="truncate text-zinc-700">{selectedOption?.label}</span>
        </div>
        <FiChevronDown
          className={`w-3.5 h-3.5 text-zinc-400 transition-transform duration-200 ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      {isOpen &&
        ReactDOM.createPortal(
          <div
            style={{
              top: `${coords.top}px`,
              left: `${coords.left}px`,
              minWidth: `${coords.width}px`,
            }}
            className="portal-dropdown-menu fixed bg-white border border-zinc-200 rounded-lg shadow-xl py-1 z-[9999] animate-in fade-in zoom-in-95 duration-100 max-h-56 overflow-y-auto"
          >
            {label && (
              <div className="px-3 py-1 border-b border-zinc-100 text-[10px] font-bold text-zinc-400 uppercase tracking-wider">
                {label}
              </div>
            )}
            {options.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => {
                  onChange(option.value);
                  setIsOpen(false);
                }}
                className={`w-full flex items-center justify-between px-3 py-1.5 text-left transition-colors text-xs cursor-pointer ${
                  value === option.value
                    ? "bg-zinc-100 text-zinc-900 font-medium"
                    : "text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900"
                }`}
              >
                <span className="truncate">{option.label}</span>
                {value === option.value && (
                  <FiCheck className="w-3.5 h-3.5 text-zinc-600 shrink-0" />
                )}
              </button>
            ))}
          </div>,
          document.body
        )}
    </div>
  );
}

export default function StudentListTab() {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Selected Student for Full Detail View
  const [activeDetailStudent, setActiveDetailStudent] = useState(null);

  // Modals
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Filter & Pagination state
  const [search, setSearch] = useState("");
  const [gender, setGender] = useState("");
  const [classroom, setClassroom] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalStudents, setTotalStudents] = useState(0);

  // Form State
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    gender: "M",
    student_id_number: "",
    profile_picture: null,
  });

  // Toast State
  const [toast, setToast] = useState({
    show: false,
    message: "",
    type: "success",
  });

  const showToast = useCallback((message, type = "success") => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast({ show: false, message: "", type: "success" });
    }, 3000);
  }, []);

  const GENDER_OPTIONS = [
    { label: "All Genders", value: "" },
    { label: "Male", value: "M" },
    { label: "Female", value: "F" },
  ];

  const fetchStudents = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (search) params.append("search", search);
      if (gender) params.append("gender", gender);
      if (classroom) params.append("classrooms", classroom);
      params.append("page", currentPage);
      params.append("page_size", pageSize);

      const response = await api.get(`/students/?${params.toString()}`);
      const data = response.data !== undefined ? response.data : await response.json();

      if (data && Array.isArray(data.results)) {
        setStudents(data.results);
        setTotalStudents(data.count || 0);
        setTotalPages(Math.ceil((data.count || 0) / pageSize) || 1);
      } else if (Array.isArray(data)) {
        setStudents(data);
        setTotalStudents(data.length);
        setTotalPages(1);
      } else {
        setStudents([]);
        setTotalStudents(0);
        setTotalPages(1);
      }
    } catch (err) {
      console.error("Error loading students:", err);
      const errMsg = err.response?.data?.detail || err.message || "Failed to load students.";
      setError(errMsg);
      setStudents([]);
      setTotalStudents(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [search, gender, classroom, currentPage, pageSize]);

  useEffect(() => {
    fetchStudents();
  }, [fetchStudents]);

  const handleOpenFormModal = (student = null, e = null) => {
    if (e) e.stopPropagation();
    if (student) {
      setSelectedStudent(student);
      setFormData({
        first_name: student.first_name || "",
        last_name: student.last_name || "",
        gender: student.gender || "M",
        student_id_number: student.student_id_number || "",
        profile_picture: null,
      });
    } else {
      setSelectedStudent(null);
      setFormData({
        first_name: "",
        last_name: "",
        gender: "M",
        student_id_number: "",
        profile_picture: null,
      });
    }
    setIsFormModalOpen(true);
  };

  const handleSaveStudent = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const submitData = new FormData();
      submitData.append("first_name", formData.first_name);
      submitData.append("last_name", formData.last_name);
      submitData.append("gender", formData.gender);
      if (formData.student_id_number) {
        submitData.append("student_id_number", formData.student_id_number);
      }
      if (formData.profile_picture) {
        submitData.append("profile_picture", formData.profile_picture);
      }

      if (selectedStudent) {
        const res = await api.put(`/students/${selectedStudent.id}/`, submitData);
        if (activeDetailStudent?.id === selectedStudent.id) {
          setActiveDetailStudent({ ...activeDetailStudent, ...res.data, ...formData });
        }
        showToast("Student updated successfully!");
      } else {
        await api.post("/students/", submitData);
        showToast("Student created successfully!");
      }
      setIsFormModalOpen(false);
      fetchStudents();
    } catch (err) {
      console.error("Error saving student:", err);
      showToast(err.response?.data?.detail || "Failed to save student.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteStudent = async () => {
    if (!selectedStudent) return;
    setSubmitting(true);
    try {
      await api.delete(`/students/${selectedStudent.id}/`);
      setIsDeleteModalOpen(false);
      if (activeDetailStudent?.id === selectedStudent.id) {
        setActiveDetailStudent(null);
      }
      setSelectedStudent(null);
      showToast("Student record deleted.");
      fetchStudents();
    } catch (err) {
      console.error("Error deleting student:", err);
      showToast(err.response?.data?.detail || "Failed to delete student.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const renderModals = () => (
    <>
      {/* Add / Edit Student Modal */}
      {isFormModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-zinc-900/40 backdrop-blur-xs animate-in fade-in duration-150">
          <div className="bg-white rounded-lg border border-zinc-200 shadow-xl w-full max-w-md overflow-visible">
            <div className="flex items-center justify-between px-5 py-3.5 border-b border-zinc-100 bg-zinc-50/50 rounded-t-xl">
              <h3 className="font-semibold text-zinc-900 text-sm">
                {selectedStudent ? "Edit Student Details" : "Add New Student"}
              </h3>
              <button
                onClick={() => setIsFormModalOpen(false)}
                className="p-1 text-zinc-400 hover:text-zinc-700 rounded-md transition-colors cursor-pointer"
              >
                <FiX className="w-4 h-4" />
              </button>
            </div>

            <form onSubmit={handleSaveStudent} className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-zinc-700 mb-1">
                    First Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.first_name}
                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                    placeholder="e.g. John"
                    className="w-full px-3 py-1.5 bg-zinc-50 border border-zinc-200/80 rounded-lg text-xs font-medium text-zinc-800 focus:outline-none focus:border-zinc-400"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-zinc-700 mb-1">
                    Last Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.last_name}
                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                    placeholder="e.g. Doe"
                    className="w-full px-3 py-1.5 bg-zinc-50 border border-zinc-200/80 rounded-lg text-xs font-medium text-zinc-800 focus:outline-none focus:border-zinc-400"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-zinc-700 mb-1">Gender</label>
                  <CustomDropdown
                    value={formData.gender}
                    options={[
                      { label: "Male", value: "M" },
                      { label: "Female", value: "F" },
                    ]}
                    onChange={(val) => setFormData({ ...formData, gender: val })}
                    icon={FiUser}
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-zinc-700 mb-1">Student ID No.</label>
                  <input
                    type="text"
                    value={formData.student_id_number}
                    onChange={(e) => setFormData({ ...formData, student_id_number: e.target.value })}
                    placeholder="e.g. STD-2025-001"
                    className="w-full px-3 py-1.5 bg-zinc-50 border border-zinc-200/80 rounded-lg text-xs font-medium text-zinc-800 focus:outline-none focus:border-zinc-400"
                  />
                </div>
              </div>

              <div className="flex items-center justify-end gap-2 pt-3 border-t border-zinc-100">
                <button
                  type="button"
                  onClick={() => setIsFormModalOpen(false)}
                  className="px-3 py-1.5 border border-zinc-200 text-zinc-600 hover:bg-zinc-50 rounded-lg text-xs font-medium transition-colors cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-3.5 py-1.5 bg-zinc-900 text-white hover:bg-zinc-800 rounded-lg text-xs font-medium transition-colors shadow-xs disabled:opacity-50 cursor-pointer flex items-center gap-1.5"
                >
                  {submitting && <FiRefreshCw className="w-3.5 h-3.5 animate-spin" />}
                  {submitting ? "Saving..." : selectedStudent ? "Update Student" : "Create Student"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen &&
        createPortal(
          <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-zinc-900/40 backdrop-blur-xs">
            <div className="bg-white rounded-lg font-dynamic-title border border-zinc-200/80 shadow-2xl w-full max-w-sm p-5 space-y-4 text-center">
              <div className="w-10 h-10 rounded-full bg-red-50 text-red-600 flex items-center justify-center mx-auto">
                <FiTrash2 className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-sm font-bold text-zinc-900">Delete Student Record</h3>
                <p className="text-xs text-zinc-500 mt-1">
                  Are you sure you want to delete{" "}
                  <span className="font-bold text-zinc-800">
                    {selectedStudent
                      ? `${selectedStudent.first_name || ""} ${selectedStudent.last_name || ""}`.trim()
                      : "this student"}
                  </span>
                  ? This action cannot be undone.
                </p>
              </div>
              <div className="flex items-center justify-center gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setIsDeleteModalOpen(false)}
                  className="w-full py-1.5 border border-zinc-200 text-zinc-600 hover:bg-zinc-50 rounded-lg text-xs font-medium transition-colors cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleDeleteStudent}
                  disabled={submitting}
                  className="w-full py-1.5 bg-red-600 text-white rounded-lg text-xs font-medium hover:bg-red-700 disabled:opacity-50 transition-colors flex items-center justify-center gap-1.5 cursor-pointer"
                >
                  {submitting && <FiLoader className="w-3.5 h-3.5 animate-spin" />}
                  <span>Delete</span>
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );

  // Render StudentDetailTab when a student is selected
  if (activeDetailStudent) {
    return (
      <>
        <StudentDetailTab
          student={activeDetailStudent}
          onBack={() => setActiveDetailStudent(null)}
          onEdit={(student) => handleOpenFormModal(student)}
          onDelete={(student) => {
            setSelectedStudent(student);
            setIsDeleteModalOpen(true);
          }}
        />
        {renderModals()}
      </>
    );
  }

  // Render Table List
  return (
    <div className="space-y-4 relative">
      {/* Toast Notification Container */}
      {toast.show &&
        ReactDOM.createPortal(
          <div className="fixed bottom-5 right-5 z-[100] flex items-center gap-2.5 px-4 py-3 rounded-lg shadow-xl bg-zinc-900 text-white text-xs font-medium animate-in slide-in-from-bottom-5 duration-200">
            {toast.type === "error" ? (
              <FiAlertCircle className="w-4 h-4 text-red-400 shrink-0" />
            ) : (
              <FiCheckCircle className="w-4 h-4 text-emerald-400 shrink-0" />
            )}
            <span>{toast.message}</span>
            <button
              type="button"
              onClick={() => setToast({ show: false, message: "", type: "success" })}
              className="ml-2 text-zinc-400 hover:text-white transition-colors"
            >
              <FiX className="w-3.5 h-3.5" />
            </button>
          </div>,
          document.body
        )}

      {/* Filter and Top Navigation Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-1">
        <div className="flex flex-wrap flex-1 items-center gap-2 max-w-2xl">
          <div className="relative flex-1 min-w-[200px]">
            <FiSearch className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <input
              type="text"
              placeholder="Search by name or ID..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full pl-9 pr-3 py-1.5 bg-zinc-50 border border-zinc-200/80 rounded-lg text-xs text-zinc-800 focus:outline-none focus:border-zinc-400 transition-colors"
            />
            {search && (
              <button
                onClick={() => {
                  setSearch("");
                  setCurrentPage(1);
                }}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600 cursor-pointer"
              >
                <FiX className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          <div className="relative flex-1 min-w-[150px]">
            <CustomDropdown
              label="Filter Gender"
              value={gender}
              options={GENDER_OPTIONS}
              onChange={(val) => {
                setGender(val);
                setCurrentPage(1);
              }}
              icon={FiUser}
            />
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => handleOpenFormModal()}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-zinc-900 hover:bg-zinc-800 text-white text-xs font-medium rounded-lg transition-colors shadow-xs cursor-pointer"
          >
            <FiPlus className="w-3.5 h-3.5" />
            <span>Add Student</span>
          </button>
        </div>
      </div>

      {/* Table Section */}
      <div className="bg-white border border-zinc-200/80 rounded-lg shadow-xs overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-zinc-50/80 border-b border-zinc-200/80 text-[11px] font-bold text-zinc-500 uppercase tracking-wider">
                <th className="py-3 px-4">Student</th>
                <th className="py-3 px-4">Student ID</th>
                <th className="py-3 px-4">Gender</th>
                <th className="py-3 px-4">Classroom</th>
                <th className="py-3 px-4">Adviser</th>
                <th className="py-3 px-4">Uniform Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-200/60 text-xs text-zinc-700">
              {loading ? (
                <tr>
                  <td colSpan="7" className="py-12 text-center text-zinc-400">
                    <FiRefreshCw className="w-5 h-5 animate-spin mx-auto mb-2 text-zinc-400" />
                    Loading student records...
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan="7" className="py-12 text-center text-red-500">
                    <FiAlertCircle className="w-6 h-6 mx-auto mb-2 text-red-400" />
                    {error}
                  </td>
                </tr>
              ) : students.length === 0 ? (
                <tr>
                  <td colSpan="7" className="py-12 text-center text-zinc-400">
                    No students found matching your criteria.
                  </td>
                </tr>
              ) : (
                students.map((student) => (
                  <tr
                    key={student.id}
                    onClick={() => setActiveDetailStudent(student)}
                    className="hover:bg-zinc-50/80 transition-colors cursor-pointer group"
                  >
                    <td className="py-3 px-4 font-semibold text-zinc-900">
                      <div className="flex items-center gap-2.5">
                        {student.profile_picture ? (
                          <img
                            src={student.profile_picture}
                            alt={`${student.first_name || ""} ${student.last_name || ""}`}
                            className="w-7 h-7 rounded-full object-cover border border-zinc-200"
                          />
                        ) : (
                          <div className="w-7 h-7 rounded-full bg-zinc-100 flex items-center justify-center text-zinc-500 font-bold text-[10px] border border-zinc-200/80 group-hover:bg-zinc-200/60 transition-colors">
                            {student.first_name?.[0] || "S"}
                            {student.last_name?.[0] || ""}
                          </div>
                        )}
                        <span className="group-hover:text-blue-600 transition-colors">
                          {student.last_name}, {student.first_name}
                        </span>
                      </div>
                    </td>

                    <td className="py-3 px-4 text-zinc-600 font-mono text-[11px]">
                      {student.student_id_number || "N/A"}
                    </td>

                    <td className="py-3 px-4 text-zinc-600">
                      {student.gender === "M"
                        ? "Male"
                        : student.gender === "F"
                        ? "Female"
                        : student.gender || "—"}
                    </td>

                    <td className="py-3 px-4 text-zinc-700 font-medium">
                      {student.classroom_name || "Unassigned"}
                    </td>

                    <td className="py-3 px-4 text-zinc-500">
                      {student.adviser_name || "N/A"}
                    </td>

                    <td className="py-3 px-4">
                      {student.has_uniform_infraction ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-semibold bg-red-50 text-red-700 border border-red-200/60">
                          Infraction Today
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200/60">
                          Clear
                        </span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {!loading && !error && students.length > 0 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalCount={totalStudents}
            pageSize={pageSize}
            onPageChange={(page) => setCurrentPage(page)}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setCurrentPage(1);
            }}
          />
        )}
      </div>

      {renderModals()}
    </div>
  );
}